import { ApiProblem, isAbortError, networkProblem, readProblem } from './problem.ts';
import type { Diagnostics } from './diagnostics.ts';

/** Yenileme ve oturum kapatma isteğinin CSRF başlığı (S05): sunucu başlıksız isteği 403 ile reddeder. */
export const REFRESH_HEADER = 'X-TicariShift-Refresh';

/**
 * Oturumu sunucunun kesin olarak reddettiğini söyleyen kodlar: istemci yenilemeyi denemez, oturumu kapatır. S44: destek oturumu
 * bittiğinde (`support_session_ended`; süre, iptal ya da kapatma) panel destek oturumunun bitiş ekranını gösterir.
 */
export const SESSION_REJECTION_CODES: ReadonlySet<string> = new Set(['membership_inactive', 'tenant_suspended', 'support_session_ended']);

export type QueryValue = string | number | boolean | null | undefined | readonly (string | number | boolean)[];

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Readonly<Record<string, QueryValue>>;
  /**
   * JSON gövde. PATCH istekleri JSON Merge Patch'tir (`application/merge-patch+json`, 01 §7.9). İkili gövde (`Blob`,
   * `File`, `ArrayBuffer`; dosya yükleme, ADR-004 §2.3) olduğu gibi, kendi türüyle gider.
   */
  body?: unknown;
  headers?: Readonly<Record<string, string>>;
  signal?: AbortSignal;
  /** Oturumsuz istek: jeton eklenmez, 401'de yenileme denenmez (oturum açma adımları, aktivasyon, şifre sıfırlama). */
  anonymous?: boolean;
  /** Oturum çerezleri gider (oturum açma adımları ve aktivasyon cihaz ve refresh çerezini yazar). */
  withCredentials?: boolean;
  /** Başarılı yanıt gövdesi okunmadan döner (dosya indirme). */
  raw?: boolean;
}

/** Oturum açma ve yenileme yanıtının jeton kısmı. */
export interface SessionTokens {
  accessToken: string;
  expiresInSeconds?: number | null;
}

export type RefreshResult =
  | { readonly kind: 'ok' }
  /** Sunucu oturumu reddetti (401/403): çerez geçersiz, üyelik kapalı ya da şirket askıda. */
  | { readonly kind: 'rejected'; readonly problem: ApiProblem }
  /** Geçici arıza (ağ, 429, 5xx): oturum korunur, daha sonra yeniden denenir. */
  | { readonly kind: 'transient'; readonly problem: ApiProblem };

export type RestoreStatus = 'ok' | 'invalid' | 'transient';

/**
 * Açılışta oturumun geri yüklenmesi (S09-K1): `ok` oturum var; `invalid` sunucu reddetti (oturum açma ekranı ya da
 * `membership_inactive`/`tenant_suspended` açıklaması); `transient` bağlantı kurulamadı (oturum korunur, yeniden
 * denenir).
 */
export interface RestoreResult {
  readonly status: RestoreStatus;
  readonly problem?: ApiProblem;
}

export interface ApiClientOptions {
  /** API kökü; boşsa aynı origin (geliştirmede Vite vekili, üretimde aynı alan adı). */
  baseUrl?: string;
  /** Oturum uçlarının kökü: panel `/api/v1/auth`, backoffice `/api/platform/auth` (ADR-002). */
  authBasePath: string;
  fetch?: typeof fetch;
  /**
   * Sunucu oturumu kesin olarak reddetti (yenileme 401/403 ya da `membership_inactive`, `tenant_suspended`):
   * istemci oturumu zaten temizlemiştir; uygulama önbelleği temizler ve uygun ekranı açar.
   */
  onSessionRejected?: (problem: ApiProblem) => void;
  /**
   * Reddetme kodunun istisnası (S41): `true` dönerse oturum korunur, hata yalnız çağırana gider. Askıdaki şirkette oturum açabilen
   * İşletme sahibinin izinsiz işlemi (yönetim, mesai kaydı) de `tenant_suspended` döner; bu oturumun reddi değildir.
   */
  keepSession?: (problem: ApiProblem) => boolean;
  /**
   * Hassas işlem yeniden doğrulama istedi (403 `step_up_required`, 07 §4.3): uygulama diyaloğu açar, başarıda yeni
   * jetonu `setSession` ile yazar ve `true` döner; istek bir kez tekrarlanır. Eşzamanlı istekler tek diyaloğu bekler.
   */
  onStepUpRequired?: () => Promise<boolean>;
  /**
   * Bu sekmede çerezle yenileme yapılabilir mi (varsayılan evet). S44: destek oturumu sekmesinde jeton yenilenmez (destek oturumunun
   * refresh jetonu ve çerezi yoktur); 401 oturumun sonudur. Aynı tarayıcıdaki kişinin kendi panel oturumunun çerezi destek sekmesine
   * hiçbir zaman karışmaz.
   */
  canRefresh?: () => boolean;
  diagnostics?: Diagnostics;
}

export interface ApiClient {
  request<T>(path: string, options?: RequestOptions & { raw?: false }): Promise<T>;
  request(path: string, options: RequestOptions & { raw: true }): Promise<Response>;
  /** Oturum açma, adım ya da yenileme yanıtındaki erişim jetonu. Jeton yalnız bellektedir (S05-K5). */
  setSession(tokens: SessionTokens): void;
  clearSession(): void;
  hasSession(): boolean;
  /** Tek uçuşlu yenileme: aynı anda gelen bütün 401'ler tek refresh çağrısını bekler (S09-K1). */
  refresh(): Promise<RefreshResult>;
  /** Açılışta oturum: bellekte jeton yoksa çerezle yenilenir. */
  restore(): Promise<RestoreResult>;
  /** Oturumu kapatır: sunucu ailesini iptal eder ve çerezi siler; ağ olmasa da yerel oturum kapanır. */
  logout(): Promise<void>;
}

const JSON_TYPE = 'application/json';
const MERGE_PATCH_TYPE = 'application/merge-patch+json';
const BINARY_TYPE = 'application/octet-stream';

/** İkili gövde: JSON'a çevrilmez (dosyanın içeriği, `PUT /api/v1/files/uploads/{token}`). */
function isBinary(body: unknown): body is Blob | ArrayBuffer | ArrayBufferView {
  return (typeof Blob !== 'undefined' && body instanceof Blob) || body instanceof ArrayBuffer || ArrayBuffer.isView(body);
}

interface Attempt {
  refreshed: boolean;
  steppedUp: boolean;
}

/**
 * API çalışma zamanı (08 §11.4; eski SPA istemcisinin mantığı çerez modeline taşındı). Erişim jetonu
 * bellekte, refresh jetonu HttpOnly çerezdedir; `localStorage` jetonu yoktur. 401'de tek uçuşlu yenileme yapılır:
 * yenileme 401/403 alırsa oturum kapanır (`onSessionRejected`), 5xx, 429 ya da ağ hatasında oturum korunur ve istek
 * geçici hatayla döner. Panel ve backoffice aynı çalışma zamanını ayrı `authBasePath` ile kullanır.
 */
export function createApiClient(options: ApiClientOptions): ApiClient {
  const baseUrl = options.baseUrl ?? '';
  const fetchImpl = options.fetch ?? ((input: RequestInfo | URL, init?: RequestInit) => fetch(input, init));
  const diagnostics = options.diagnostics;

  let accessToken: string | null = null;
  let refreshing: Promise<RefreshResult> | null = null;
  let steppingUp: Promise<boolean> | null = null;

  function url(path: string, query?: Readonly<Record<string, QueryValue>>): string {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(query ?? {})) {
      if (value === undefined || value === null || value === '') {
        continue;
      }

      for (const item of Array.isArray(value) ? value : [value]) {
        search.append(key, String(item));
      }
    }

    const qs = search.toString();
    return `${baseUrl}${path}${qs ? `${path.includes('?') ? '&' : '?'}${qs}` : ''}`;
  }

  function reject(problem: ApiProblem) {
    accessToken = null;
    diagnostics?.record('session-rejected', { code: problem.code, status: problem.status });
    options.onSessionRejected?.(problem);
  }

  async function doRefresh(): Promise<RefreshResult> {
    if (options.canRefresh?.() === false) {
      // Yenilenmeyen oturum (destek oturumu sekmesi, S44): çereze hiç gidilmez.
      accessToken = null;
      return { kind: 'rejected', problem: new ApiProblem(401, { code: 'unauthorized' }) };
    }

    let response: Response;
    try {
      response = await fetchImpl(url(`${options.authBasePath}/refresh`), {
        method: 'POST',
        credentials: 'include',
        headers: { [REFRESH_HEADER]: '1', Accept: JSON_TYPE },
      });
    } catch (cause) {
      diagnostics?.record('refresh', { status: 'network' });
      return { kind: 'transient', problem: networkProblem(cause) };
    }

    diagnostics?.record('refresh', { status: response.status });
    if (response.ok) {
      let tokens: SessionTokens;
      try {
        tokens = (await response.json()) as SessionTokens;
      } catch (cause) {
        // Gövde okunamadı (ör. araya giren bir vekilin HTML sayfası): oturum korunur, geçici arıza sayılır.
        return { kind: 'transient', problem: networkProblem(cause) };
      }

      if (typeof tokens?.accessToken !== 'string' || tokens.accessToken === '') {
        return { kind: 'transient', problem: networkProblem(undefined) };
      }

      accessToken = tokens.accessToken;
      return { kind: 'ok' };
    }

    const problem = await readProblem(response);
    if (response.status === 401 || response.status === 403) {
      accessToken = null;
      return { kind: 'rejected', problem };
    }

    return { kind: 'transient', problem };
  }

  function refresh(): Promise<RefreshResult> {
    refreshing ??= doRefresh().finally(() => {
      refreshing = null;
    });
    return refreshing;
  }

  function stepUp(): Promise<boolean> {
    const handler = options.onStepUpRequired;
    if (!handler) {
      return Promise.resolve(false);
    }

    steppingUp ??= handler()
      .catch(() => false)
      .finally(() => {
        steppingUp = null;
      });
    return steppingUp;
  }

  async function send(path: string, request: RequestOptions, attempt: Attempt): Promise<Response> {
    const method = request.method ?? 'GET';
    const authenticated = !request.anonymous;

    if (authenticated && accessToken === null) {
      if (attempt.refreshed) {
        // Yenilemeden sonra jeton yine yok (eşzamanlı bir ret temizledi): oturum yoktur.
        throw new ApiProblem(401, { code: 'unauthorized' });
      }

      // Jeton yok (açılış, başka sekmede kapanan oturum): önce çerezle yenilenir.
      const renewed = await refresh();
      if (renewed.kind === 'rejected') {
        reject(renewed.problem);
        throw renewed.problem;
      }

      if (renewed.kind === 'transient') {
        throw renewed.problem;
      }

      attempt = { ...attempt, refreshed: true };
    }

    const headers: Record<string, string> = { Accept: JSON_TYPE, ...request.headers };
    const binary = isBinary(request.body);
    if (binary) {
      headers['Content-Type'] ??= (request.body instanceof Blob && request.body.type) || BINARY_TYPE;
    } else if (request.body !== undefined) {
      headers['Content-Type'] = method === 'PATCH' ? MERGE_PATCH_TYPE : JSON_TYPE;
    }

    if (authenticated && accessToken !== null) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    let response: Response;
    try {
      response = await fetchImpl(url(path, request.query), {
        method,
        headers,
        body: binary ? (request.body as BodyInit) : request.body === undefined ? undefined : JSON.stringify(request.body),
        credentials: request.withCredentials ? 'include' : 'same-origin',
        signal: request.signal,
      });
    } catch (cause) {
      if (isAbortError(cause)) {
        throw cause;
      }

      diagnostics?.record('request', { method, path, status: 'network' });
      throw networkProblem(cause);
    }

    if (response.ok) {
      return response;
    }

    diagnostics?.record('request', { method, path, status: response.status });
    const problem = await readProblem(response);

    if (authenticated && SESSION_REJECTION_CODES.has(problem.code) && !options.keepSession?.(problem)) {
      reject(problem);
      throw problem;
    }

    if (authenticated && response.status === 401 && !attempt.refreshed) {
      const renewed = await refresh();
      if (renewed.kind === 'ok') {
        return send(path, request, { ...attempt, refreshed: true });
      }

      if (renewed.kind === 'rejected') {
        reject(renewed.problem);
      }

      throw renewed.problem;
    }

    if (authenticated && response.status === 403 && problem.code === 'step_up_required' && !attempt.steppedUp) {
      if (await stepUp()) {
        return send(path, request, { ...attempt, steppedUp: true });
      }
    }

    throw problem;
  }

  async function request(path: string, request: RequestOptions = {}): Promise<unknown> {
    const response = await send(path, request, { refreshed: false, steppedUp: false });
    if (request.raw) {
      return response;
    }

    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return undefined;
    }

    const contentType = response.headers.get('content-type') ?? '';
    return contentType.includes('json') ? response.json() : undefined;
  }

  return {
    request: request as ApiClient['request'],
    setSession(tokens) {
      accessToken = tokens.accessToken;
    },
    clearSession() {
      accessToken = null;
    },
    hasSession() {
      return accessToken !== null;
    },
    refresh,
    async restore() {
      if (accessToken !== null) {
        return { status: 'ok' };
      }

      const result = await refresh();
      const restored: RestoreResult =
        result.kind === 'ok'
          ? { status: 'ok' }
          : { status: result.kind === 'rejected' ? 'invalid' : 'transient', problem: result.problem };
      diagnostics?.record('restore', { status: restored.status, code: restored.problem?.code });
      return restored;
    },
    async logout() {
      try {
        await fetchImpl(url(`${options.authBasePath}/logout`), {
          method: 'POST',
          credentials: 'include',
          headers: { [REFRESH_HEADER]: '1' },
        });
      } catch {
        // Ağ yoksa sunucudaki aile süresiyle kapanır; yerel oturum yine kapanır.
      } finally {
        accessToken = null;
      }
    },
  };
}

/**
 * İndirilen dosya: gövde ve `Content-Disposition`'daki ad (RFC 6266; Türkçe adlar `filename*=UTF-8''…`). Ad yoksa
 * `fallbackName`.
 */
export async function readBlob(response: Response, fallbackName: string): Promise<{ blob: Blob; fileName: string }> {
  const disposition = response.headers.get('content-disposition') ?? '';
  const encoded = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(disposition)?.[1];
  const plain = /filename\s*=\s*("?)([^";]*)\1/.exec(disposition)?.[2];
  let fileName = fallbackName;
  if (encoded) {
    try {
      fileName = decodeURIComponent(encoded.trim());
    } catch {
      fileName = plain?.trim() || fallbackName;
    }
  } else if (plain?.trim()) {
    fileName = plain.trim();
  }

  return { blob: await response.blob(), fileName };
}
